import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    InputAdornment,
    Box,
    Typography,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Chip,
    Divider,
    IconButton,
    CircularProgress,
    Paper,
    Fade
} from '@mui/material';
import {
    Search as SearchIcon,
    Person as PersonIcon,
    AccountBalanceWallet as WalletIcon,
    Receipt as TransactionIcon,
    Close as CloseIcon,
    TrendingUp as TrendingIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { API_BASE_URL } from '../../utils/constants';

const SearchDialog = ({ open, onClose }) => {
    const { t } = useTranslation();
    const [query, setQuery] = useState('');
    const [results, setResults] = useState({ transactions: [], users: [], addresses: [] });
    const [loading, setLoading] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const navigate = useNavigate();
    const theme = useTheme();
    const inputRef = useRef(null);
    const timeoutRef = useRef(null);

    // Focus input when dialog opens
    useEffect(() => {
        if (open && inputRef.current) {
            setTimeout(() => {
                inputRef.current.focus();
            }, 100);
        }
    }, [open]);

    // Clear search when dialog closes
    useEffect(() => {
        if (!open) {
            setQuery('');
            setResults({ transactions: [], users: [], addresses: [] });
            setShowResults(false);
        }
    }, [open]);

    // Debounced search
    useEffect(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        if (query.length >= 2) {
            timeoutRef.current = setTimeout(() => {
                performSearch(query);
            }, 300);
        } else {
            setResults({ transactions: [], users: [], addresses: [] });
            setShowResults(false);
        }

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [query]);

    const performSearch = async (searchQuery) => {
        setLoading(true);
        try {
            const response = await fetch(`${ API_BASE_URL }/explorer/search?q=${ encodeURIComponent(searchQuery) }`);
            const data = await response.json();
            setResults(data.results || { transactions: [], users: [], addresses: [] });
            setShowResults(true);
        } catch (error) {
            console.error('Search error:', error);
            setResults({ transactions: [], users: [], addresses: [] });
        } finally {
            setLoading(false);
        }
    };

    const handleResultClick = (type, value) => {
        onClose();
        setQuery('');
        setResults({ transactions: [], users: [], addresses: [] });
        setShowResults(false);

        switch (type) {
            case 'user':
                window.open(`/explorer/user/${ value }`);
                break;
            case 'address':
                window.open(`/explorer/address/${ value }`);
                break;
            case 'transaction':
                window.open(`/explorer/transaction/${ value.txId }`);
                break;
            default:
                break;
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Escape') {
            onClose();
        }
    };

    const formatAddress = (address) => {
        if (address.length > 20) {
            return `${ address.slice(0, 12) }...${ address.slice(-12) }`;
        }
        return address;
    };

    const formatTxId = (txId) => {
        if (txId.length > 16) {
            return `${ txId.slice(0, 12) }...${ txId.slice(-12) }`;
        }
        return txId;
    };

    const totalResults = results.transactions.length + results.users.length + results.addresses.length;

    const recentSearches = [
        'bc1qxy2kgdygjrsqtzq2n0yrf2493p83kkfjhx0wlh',
        'user_123',
        t('explorer.latestTransactions')
    ];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    minHeight: '60vh',
                    maxHeight: '80vh',
                    backgroundColor: theme.palette.background.paper,
                    backgroundImage: 'none'
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: `1px solid ${ theme.palette.divider }`,
                pb: 2
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SearchIcon color="primary" />
                    <Typography variant="h6">{t('explorer.searchBlockchainExplorer')}</Typography>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
                {/* Search Input */}
                <Box sx={{ p: 3, borderBottom: `1px solid ${ theme.palette.divider }` }}>
                    <TextField
                        ref={inputRef}
                        fullWidth
                        placeholder={t('explorer.searchPlaceholder')}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={handleKeyDown}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                            endAdornment: loading && (
                                <InputAdornment position="end">
                                    <CircularProgress size={20} />
                                </InputAdornment>
                            ),
                            sx: {
                                fontSize: '1.1rem',
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 3
                                }
                            }
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: theme.palette.mode === 'dark'
                                    ? 'rgba(255, 255, 255, 0.05)'
                                    : 'rgba(0, 0, 0, 0.02)'
                            }
                        }}
                    />
                </Box>

                {/* Results Section */}
                <Box sx={{ maxHeight: '50vh', overflow: 'auto' }}>
                    {!query && (
                        <Box sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TrendingIcon color="primary" />
                                {t('explorer.recentSearches')}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                                {recentSearches.map((search, index) => (
                                    <Chip
                                        key={index}
                                        label={search}
                                        variant="outlined"
                                        clickable
                                        onClick={() => setQuery(search)}
                                        sx={{ fontFamily: search.includes('bc1') ? 'monospace' : 'inherit' }}
                                    />
                                ))}
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                                💡 {t('explorer.proTips')}:
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                • {t('explorer.tipSearchBy')}
                                <br />
                                • {t('explorer.tipPartialMatches')}
                                <br />
                                • {t('explorer.tipPressEsc')}
                            </Typography>
                        </Box>
                    )}

                    {showResults && (
                        <Fade in={showResults}>
                            <Box>
                                {totalResults === 0 ? (
                                    <Box sx={{ p: 4, textAlign: 'center' }}>
                                        <SearchIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                                        <Typography variant="h6" color="text.secondary" gutterBottom>
                                            {t('explorer.noResultsFound')}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {t('explorer.noMatchesFound', { query })}
                                        </Typography>
                                    </Box>
                                ) : (
                                    <List dense>
                                        {/* Users Section */}
                                        {results.users.length > 0 && (
                                            <>
                                                <ListItem sx={{ py: 2, backgroundColor: theme.palette.action.hover }}>
                                                    <ListItemIcon>
                                                        <PersonIcon color="primary" />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={
                                                            <Typography variant="subtitle1" color="primary" fontWeight="bold">
                                                                {t('explorer.users')} ({results.users.length})
                                                            </Typography>
                                                        }
                                                    />
                                                </ListItem>
                                                {results.users.slice(0, 5).map((user) => (
                                                    <ListItem
                                                        key={user}
                                                        button
                                                        onClick={() => handleResultClick('user', user)}
                                                        sx={{
                                                            pl: 4,
                                                            '&:hover': {
                                                                backgroundColor: theme.palette.action.hover
                                                            }
                                                        }}
                                                    >
                                                        <ListItemIcon>
                                                            <PersonIcon fontSize="small" />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={user}
                                                            secondary="User Profile"
                                                        />
                                                    </ListItem>
                                                ))}
                                                {results.users.length > 5 && (
                                                    <ListItem sx={{ pl: 4 }}>
                                                        <ListItemText>
                                                            <Chip
                                                                size="small"
                                                                label={`+${ results.users.length - 5 } more users`}
                                                                variant="outlined"
                                                                color="primary"
                                                            />
                                                        </ListItemText>
                                                    </ListItem>
                                                )}
                                                <Divider />
                                            </>
                                        )}

                                        {/* Addresses Section */}
                                        {results.addresses.length > 0 && (
                                            <>
                                                <ListItem sx={{ py: 2, backgroundColor: theme.palette.action.hover }}>
                                                    <ListItemIcon>
                                                        <WalletIcon color="secondary" />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={
                                                            <Typography variant="subtitle1" color="secondary" fontWeight="bold">
                                                                Addresses ({results.addresses.length})
                                                            </Typography>
                                                        }
                                                    />
                                                </ListItem>
                                                {results.addresses.slice(0, 5).map((address) => (
                                                    <ListItem
                                                        key={address}
                                                        button
                                                        onClick={() => handleResultClick('address', address)}
                                                        sx={{
                                                            pl: 4,
                                                            '&:hover': {
                                                                backgroundColor: theme.palette.action.hover
                                                            }
                                                        }}
                                                    >
                                                        <ListItemIcon>
                                                            <WalletIcon fontSize="small" />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={formatAddress(address)}
                                                            secondary="Bitcoin Address"
                                                            primaryTypographyProps={{ fontFamily: 'monospace' }}
                                                        />
                                                    </ListItem>
                                                ))}
                                                {results.addresses.length > 5 && (
                                                    <ListItem sx={{ pl: 4 }}>
                                                        <ListItemText>
                                                            <Chip
                                                                size="small"
                                                                label={`+${ results.addresses.length - 5 } more addresses`}
                                                                variant="outlined"
                                                                color="secondary"
                                                            />
                                                        </ListItemText>
                                                    </ListItem>
                                                )}
                                                <Divider />
                                            </>
                                        )}

                                        {/* Transactions Section */}
                                        {results.transactions.length > 0 && (
                                            <>
                                                <ListItem sx={{ py: 2, backgroundColor: theme.palette.action.hover }}>
                                                    <ListItemIcon>
                                                        <TransactionIcon color="info" />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={
                                                            <Typography variant="subtitle1" color="info" fontWeight="bold">
                                                                {t('explorer.transactions')} ({results.transactions.length})
                                                            </Typography>
                                                        }
                                                    />
                                                </ListItem>
                                                {results.transactions.slice(0, 5).map((tx) => (
                                                    <ListItem
                                                        key={tx.txId}
                                                        button
                                                        onClick={() => handleResultClick('transaction', tx)}
                                                        sx={{
                                                            pl: 4,
                                                            '&:hover': {
                                                                backgroundColor: theme.palette.action.hover
                                                            }
                                                        }}
                                                    >
                                                        <ListItemIcon>
                                                            <TransactionIcon fontSize="small" />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={formatTxId(tx.txId)}
                                                            secondary={
                                                                <Box>
                                                                    <Typography variant="caption" display="block">
                                                                        {tx.type === 'deposit' ? '📥 Deposit' : '📤 Withdrawal'}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {tx.amount} BTC • {tx.user}
                                                                    </Typography>
                                                                </Box>
                                                            }
                                                            primaryTypographyProps={{ fontFamily: 'monospace' }}
                                                        />
                                                    </ListItem>
                                                ))}
                                                {results.transactions.length > 5 && (
                                                    <ListItem sx={{ pl: 4 }}>
                                                        <ListItemText>
                                                            <Chip
                                                                size="small"
                                                                label={t('explorer.moreTransactions', { count: results.transactions.length - 5 })}
                                                                variant="outlined"
                                                                color="info"
                                                            />
                                                        </ListItemText>
                                                    </ListItem>
                                                )}
                                            </>
                                        )}
                                    </List>
                                )}
                            </Box>
                        </Fade>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default SearchDialog;
